import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { Briefcase, LogOut } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { apiClient, setPortalToken } from '@/lib/api-client';
import { usePortalValidate } from '@/hooks/use-projects';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const PORTAL_API = '/api/v1/projects/portal';

export function PortalLayout() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Keep the portal access token in the axios interceptor (sent as the
  // X-Portal-Token header). The backend sets an HttpOnly cookie for
  // direct-download URLs, so it is never readable from JavaScript.
  const activeToken = token ?? '';
  setPortalToken(activeToken || null);

  const { data: session, isLoading, isError } = usePortalValidate(activeToken);

  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSignOut = useCallback(async () => {
    // Ask the backend to clear the HttpOnly token cookie, then drop the
    // in-memory header token, any legacy localStorage copy, and all cached
    // portal data so the next signed-out view cannot leak prior customer data.
    try {
      await apiClient.post(`${PORTAL_API}/logout/`);
    } catch {
      // Best-effort logout; navigate away regardless.
    }
    localStorage.removeItem('portal_token');
    setPortalToken(null);
    queryClient.removeQueries({ queryKey: ['portal'] });
    // Navigate to the portal root with a signed-out message
    navigate('/portal/signed-out');
  }, [navigate, queryClient]);

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
    }
    idleTimer.current = setTimeout(() => {
      handleSignOut();
    }, IDLE_TIMEOUT_MS);
  }, [handleSignOut]);

  // Set up idle-timeout listeners
  useEffect(() => {
    const events: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];

    // Start timer
    resetIdleTimer();

    const handler = () => resetIdleTimer();
    events.forEach((evt) => window.addEventListener(evt, handler, { passive: true }));

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((evt) => window.removeEventListener(evt, handler));
    };
  }, [resetIdleTimer]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isError || !session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Portal Link</h1>
          <p className="text-gray-600">This portal link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Briefcase className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Client Portal</h1>
                <p className="text-sm text-gray-500">
                  Signed in as <span className="font-medium text-gray-700">{session.client_name}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 hidden sm:inline">{session.email}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="flex items-center gap-1.5"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <Outlet />
    </div>
  );
}
